<template>
    <component
        :is="dynamicComponent"
        v-if="dynamicComponent"
        v-bind="componentProps"
    />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';

const props = defineProps({
    blok: {
        type: Object,
        required: true,
    },
    nextBlock: {
        type: Object,
        required: false,
        default: null,
    },
});

if (!props.blok?.component) {
    console.error('Storyblok block missing component type:', props.blok);
}

const componentMap: Record<string, () => Promise<any>> = {
    hero: () => import('~/storyblok/HeroStoryblok.vue'),
    hero_fullscreen_image: () =>
        import('~/storyblok/HeroFullscreenImageStoryblok.vue'),
    page_intro: () => import('~/storyblok/PageIntroStoryblok.vue'),
    circle_button_carousel: () =>
        import('~/storyblok/CircleButtonCarouselStoryblok.vue'),
    differentiator_grid: () =>
        import('~/storyblok/DifferentiatorGridStoryblok.vue'),
    image_content: () => import('~/storyblok/ImageContentStoryblok.vue'),
    video_content: () => import('~/storyblok/VideoContentStoryblok.vue'),
    text_content: () => import('~/storyblok/TextContentStoryblok.vue'),
    product_carousel: () => import('~/storyblok/ProductCarouselBlock.vue'),
    product_packaging_showcase: () =>
        import('~/storyblok/ProductPackagingShowcaseStoryblok.vue'),
    image_grid_content: () =>
        import('~/storyblok/ImageGridContentStoryblok.vue'),
    testimonial_content_slider: () =>
        import('~/storyblok/TestimonialContentSliderStoryblok.vue'),
    collapse_card_group: () =>
        import('~/storyblok/CollapseCardGroupStoryblok.vue'),
    cta_section: () => import('~/storyblok/CtaSectionStoryblok.vue'),
    product_filter: () => import('~/storyblok/ProductFilterStoryblok.vue'),
    blog_article_grid: () => import('~/storyblok/BlogArticleGridStoryblok.vue'),
    product_anatomy: () => import('~/storyblok/ProductAnatomyStoryblok.vue'),
    article_card_content: () =>
        import('~/storyblok/ArticleCardContentStoryblok.vue'),
    process_select: () => import('~/storyblok/ProcessSelectStoryblok.vue'),
    faq_category_view: () => import('~/storyblok/FaqCategoryViewStoryblok.vue'),
    table_of_contents_view: () =>
        import('~/storyblok/TableOfContentsViewStoryblok.vue'),
    trade_show_grid: () => import('~/storyblok/TradeShowGridStoryblok.vue'),
    lead_times_view: () => import('~/storyblok/LeadTimesViewStoryblok.vue'),
    how_it_works_showcase: () =>
        import('~/storyblok/HowItWorksShowcaseStoryblok.vue'),
    animated_image_content: () =>
        import('~/storyblok/AnimatedImageContentStoryblok.vue'),
    product_card_content: () =>
        import('~/storyblok/ProductCardContentStoryblok.vue'),
    sample_pack_selector: () =>
        import('~/storyblok/SamplePackSelectorStoryblok.vue'),
    inspiration_gallery: () =>
        import('~/storyblok/InspirationGalleryStoryblok.vue'),
    product_resource_gallery: () =>
        import('~/storyblok/ProductResourceGalleryStoryblok.vue'),
    design_resource_gallery: () =>
        import('~/storyblok/DesignResourceGalleryStoryblok.vue'),
    designer_detail_section: () =>
        import('~/storyblok/DesignerDetailStoryblok.vue'),
    template_gallery: () => import('~/storyblok/TemplateGalleryStoryblok.vue'),
    contact_form: () => import('~/storyblok/ContactFormStoryblok.vue'),
    credit_application_form: () =>
        import('~/storyblok/CreditApplicationFormStoryblok.vue'),
    product_finder: () => import('~/storyblok/ProductFinderBlock.vue'),
    trustpilot_testimonials_grid: () =>
        import('~/storyblok/TrustpilotTestimonialsGridStoryblok.vue'),
    coupon_code: () => import('~/storyblok/CouponCodeStoryblok.vue'),
    tabbed_product_carousel: () =>
        import('~/storyblok/TabbedProductCarouselStoryblok.vue'),
    image_carousel: () => import('~/storyblok/ImageCarouselStoryblok.vue'),
    comparison_grid: () => import('~/storyblok/ComparisonGridStoryblok.vue'),
    table: () => import('~/storyblok/TableStoryblok.vue'),
    comparison_copy: () => import('~/storyblok/ComparisonCopyStoryblok.vue'),
    comparison_supplier_choice: () =>
        import('~/storyblok/ComparisonSupplierChoiceStoryblok.vue'),
};

const asyncComponentMap: Record<
    string,
    ReturnType<typeof defineAsyncComponent>
> = {};

for (const [key, loader] of Object.entries(componentMap)) {
    asyncComponentMap[key] = defineAsyncComponent(loader as () => Promise<any>);
}

const dynamicComponent = computed(() => {
    const componentType = props.blok?.component;
    if (!componentType || !asyncComponentMap[componentType]) {
        console.warn(`Unknown component type: ${componentType}`, props.blok);
        return null;
    }

    return asyncComponentMap[componentType];
});

const componentProps = computed(() => {
    const usesBlockProp = [
        'product_finder',
        'trustpilot_testimonials_grid',
    ].includes(props.blok?.component);

    return {
        [usesBlockProp ? 'block' : 'blok']: props.blok,
        nextBlock: props.nextBlock,
    };
});
</script>
